import type { Action, PayloadAction } from '@reduxjs/toolkit';
import type { AxiosRequestConfig } from 'axios';
import { type Collection, Map as ImMap, Record, type RecordOf } from 'immutable';

import { getCommonConfig, getLogger } from '../../utils/core';
import type { AsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import type RootState from '../../utils/redux-store/rootState';
import type { Meta } from './actionHelpers';
import { isRecordOf } from './recordHelpers';

// Re-exported for callers; it lives apart so modules can use it without this one's dependencies.
export { isRecordOf };

const logger = getLogger('resourceStoreTypes');

export type IdentityLike = { id?: string | null; clientId?: string | null } | null | undefined;

/**
 * Returns every usable identity key for relationship matching, preserving both `id` and `clientId`.
 *
 * Most callers should use `getId` / `getOptionalId`, which collapse identity down to a single
 * canonical key and prefer `id` once it exists.
 *
 * This helper is intentionally different: linked resources are not always hydrated or normalized
 * at the same time. A transfer or split link may still point at the pre-persist `clientId` after
 * the target resource has a server `id`, or may carry a stale `id` while the `clientId` remains
 * the only reliable bridge between the two records. In those cases we need to check both keys.
 */
export function getIdentityKeys(resource: IdentityLike): string[] {
  return [resource?.id, resource?.clientId].filter((value): value is string => Boolean(value)).map(String);
}

/**
 * Nullable counterpart to `getId`.
 * Use this when a resource may legitimately be missing both identifiers during probing/lookups.
 * Use `getId` when the caller requires a stable identifier and should fail fast if one is missing.
 */
export function getOptionalId<R extends IdentityLike>(resource: R): string | null {
  return resource?.id || resource?.clientId || null;
}

export function getId<R extends Exclude<IdentityLike, null | undefined>>(resource: R): string {
  const id = getOptionalId(resource);
  if (!id) {
    throw new Error('Trying to use an id from a resource which is missing both clientId and id');
  }
  return id;
}

export type QcsResourcesResponse<T> = {
  resources: T[];
  metaData?: {
    pageSize?: number;
    limit?: number;
    asOf: string;
    currentPage: number;
    offset: number;
    totalPages: number;
    totalSize: number;
    nextLink?: string;
  };
};

export type BaseResource = {
  clientId?: string | null;
  id?: string | null;
  isDeleted?: boolean | null;
};

export type QcsSyncResourceStoreProps<T extends BaseResource> = {
  resourcesById: ImMap<string, RecordOf<T>>;

  resourceName?: string | null;
  lastSyncDate?: string | null;
  loadPending: boolean;
  isLoading: boolean;
  error?: unknown;
};
export type QcsSyncResourceStore<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T> = QcsSyncResourceStoreProps<T>,
> = RecordOf<V>;

/** The shared store fields' defaults, for stores that add fields of their own. */
export const getQcsSyncResourceStoreDefaults = <T extends BaseResource>(): QcsSyncResourceStoreProps<T> => ({
  resourcesById: ImMap(),
  resourceName: null,
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  error: undefined,
});

export function createTypedQcsSyncResourceStoreFactory<T extends BaseResource>(
  defaults?: Partial<QcsSyncResourceStoreProps<T>>,
) {
  return Record<QcsSyncResourceStoreProps<T>>({ ...getQcsSyncResourceStoreDefaults<T>(), ...defaults });
}

// Deprecated, use createTypedQcsSyncResourceStoreFactory instead if you can
export const mkQcsSyncResourceStore = createTypedQcsSyncResourceStoreFactory<BaseResource>();

export type QcsSyncResourcesPayloadProps<T extends BaseResource> = {
  asyncDispatch?: AsyncDispatch;
  resources: RecordOf<T>[];
  doMerge?: boolean;
  replaceCollection?: boolean;
  lastSyncDate?: string | null;
  error?: unknown;
};
export type QcsSyncResourcesPayload<T extends BaseResource> =
  | QcsSyncResourcesPayloadProps<T>
  | RecordOf<QcsSyncResourcesPayloadProps<T>>;
export function createTypedQcsSyncResourcePayloadFactory<T extends BaseResource>() {
  return Record<QcsSyncResourcesPayloadProps<T>>({
    asyncDispatch: undefined,
    resources: [],
    doMerge: true,
    replaceCollection: false,
    lastSyncDate: null,
    error: undefined,
  });
}
// Deprecated, use createTypedQcsSyncResourcePayloadFactory instead if you can
export const mkQcsSyncResourcesPayload = createTypedQcsSyncResourcePayloadFactory<BaseResource>();

export type PollConfigProps = {
  /** Gets the latest response, or the response of a failed request when there is one. */
  isComplete: (response: unknown, attempt: number) => boolean;
  attempts: number;
  pause: number;
};
export type PollConfig = RecordOf<PollConfigProps>;
export const mkPollConfig = Record<PollConfigProps>({
  isComplete: () => false,
  attempts: 10,
  pause: 300,
});

/** An action the shared sagas and reducers handle; reducers also get the middleware's asyncDispatch. */
export type CoreAction<Payload = unknown, ActionMeta = Meta | undefined> = PayloadAction<
  Payload,
  string,
  ActionMeta
> & {
  error?: boolean;
  asyncDispatch?: AsyncDispatch;
};

/** How the shared sagas see an action's meta. The config is left out: each resource type has its own. */
export type SagaMeta = Omit<Meta, 'config'>;

/** The action a shared saga handles; a saga run without one gets an empty action with null meta. */
export type ResourceSagaAction<Payload = unknown> = PayloadAction<Payload> & {
  meta?: SagaMeta | null;
  error?: boolean;
  asyncDispatch?: AsyncDispatch;
};

/** A callback whose parameters compare bivariantly (method syntax): it declares the shapes it handles. */
export type SagaCallback<Args extends unknown[], Result> = { bivarianceHack(...args: Args): Result }['bivarianceHack'];

/** The meta success and failure actions get: the request's, without its promise callbacks or config. */
export type ResponseMeta = Omit<Meta, 'resolve' | 'reject' | 'config'>;

/** What the sagas pass a success action; `void` admits success actions that ignore it. */
export type ResourceSuccessPayload<T extends BaseResource> =
  | QcsSyncResourcesPayload<T>
  | ReturnType<QcsSyncResourcesConfigProps<T>['transformResourceWithResponse']>
  | ReturnType<QcsSyncResourcesConfigProps<T>['transformResourcesWithResponse']>
  | void;

/** A success action creator of a resource config, which takes one of the payloads the sagas send. */
export type ResourceActionCreator<T extends BaseResource> = SagaCallback<
  [payload: ResourceSuccessPayload<T>, meta?: ResponseMeta],
  Action
>;

/** A failure action creator of a resource config, which gets whatever the request threw. */
export type ResourceFailureActionCreator = (payload: unknown, meta: ResponseMeta) => Action;

export type QcsError = {
  title: string;
  httpStatus: number;
  code: string;
  extData: unknown;
};

/** What QCS returns for a resource write: the resource, or its id and the ids of what it contains. */
export type QcsResourceResponseData<T extends BaseResource> = T & {
  resource?: T;
  resources?: T[];
  correlationIds?: { [id: string]: string };
};

/** The resources of a batch write: an array, or an Immutable collection whose values are the resources. */
export type ResourceBatch<T extends BaseResource> =
  | ReadonlyArray<RecordOf<T> | T>
  | Collection<unknown, RecordOf<T> | T>;

/** What QCS returns for each resource of a batch write, in request order. */
export type QcsBatchResponseItem<T extends BaseResource> = {
  resource?: T & { resource?: T; status?: string };
  errors?: QcsError[];
};

/** `Command` is what a create or update sends instead of a resource, for endpoints that take a command. */
export type QcsSyncResourcesConfigProps<T extends BaseResource, Command = never> = {
  resourceName: string;
  resourceBaseUrl: string | SagaCallback<[action: ResourceSagaAction], string>;
  axiosConfig: AxiosRequestConfig | undefined;
  getLastSyncDate?: ((state: RootState) => string | null | undefined) | null;
  pollConfig: PollConfig;
  applyResourceFromCRUDResponse?: boolean;
  handleRetry:
    | boolean
    | SagaCallback<[error: unknown, action: ResourceSagaAction, config: QcsSyncResourcesConfig<T, Command>], boolean>;
  ignoreError?:
    | SagaCallback<
        [error: unknown, action: ResourceSagaAction, config: QcsSyncResourcesConfig<T, Command>],
        boolean | string
      >
    | boolean;
  /**
   * When set, qcsSyncUpdateResources sends payloads larger than this as sequential requests of at
   * most this many resources, so a single request cannot exceed the service timeout. Payloads at or
   * below this size, and configs that leave it unset, take the single-request path.
   */
  maxResourcesPerRequest?: number;

  // The request transforms get the dispatched payload, and what they return is sent as the request body.
  transformClientResourceToRequestData: SagaCallback<[resource: RecordOf<T> | T | Command], unknown>;
  transformResponseToResource: (response: { data: T }) => RecordOf<T> | null | undefined;
  transformResponseToResources: (response?: { data?: { resources?: T[] } } | null) => RecordOf<T>[] | null | undefined;
  transformResourceWithResponse: SagaCallback<
    [
      resource: RecordOf<T> | T | Command,
      response: { data?: QcsResourceResponseData<T> },
      config: QcsSyncResourcesConfig<T, Command>,
    ],
    RecordOf<T> | Array<RecordOf<T>> | T | null | undefined
  >;
  transformResourcesWithResponse: SagaCallback<
    [resources: ResourceBatch<T>, response: { data?: T & { items?: QcsBatchResponseItem<T>[] } }],
    Array<RecordOf<T> | T | undefined> | undefined | null
  >;
  transformResourceToRequestData: SagaCallback<[resource: RecordOf<T> | T | Command], unknown>;
  transformResourcesToRequestData: SagaCallback<[resources: ResourceBatch<T>], unknown>;
  transformServerResourceToClientResource: (data: T) => RecordOf<T> | null | undefined;

  successAction?: ResourceActionCreator<T>;
  failureAction?: ResourceFailureActionCreator;
};
export type QcsSyncResourcesConfig<T extends BaseResource, Command = never> = RecordOf<
  QcsSyncResourcesConfigProps<T, Command>
>;

const getQcsSyncResourcesConfigDefaults = <T extends BaseResource>(): QcsSyncResourcesConfigProps<T> => ({
  resourceName: 'unknown-resource',
  resourceBaseUrl: 'resources',
  axiosConfig: undefined,
  getLastSyncDate: undefined,
  pollConfig: mkPollConfig(),
  applyResourceFromCRUDResponse: undefined,
  handleRetry: getCommonConfig()?.handle_retry ?? false,
  ignoreError: undefined,
  maxResourcesPerRequest: undefined,

  // main transformers to override
  transformServerResourceToClientResource: (data) => data && Record(data)(data),
  transformClientResourceToRequestData: (resource) => (isRecordOf(resource) ? resource.toJS() : resource),

  // framework transformers that generally shouldn't need to change
  transformResponseToResource(response) {
    // TODO: all other response handling code looks for response.data.resource. Are we sure there shouldn't be a resource field?
    return this.transformServerResourceToClientResource(response.data);
  },
  transformResponseToResources(response) {
    const clientResources: RecordOf<T>[] = [];

    response?.data?.resources?.forEach((r) => {
      const clientResource = this.transformServerResourceToClientResource(r);
      if (clientResource) clientResources.push(clientResource);
    });

    return clientResources;
  },
  transformResourceWithResponse(resource, response, config) {
    if (response?.data?.resource && config?.applyResourceFromCRUDResponse == null) {
      logger.warn(
        `${config?.resourceName}: enable/disable "response" property handling explicitly (applyResourceFromCRUDResponse = true/false)`,
      );
    }
    if (response?.data?.resource && config?.applyResourceFromCRUDResponse) {
      // apply resource
      if (isRecordOf(resource)) {
        const fieldsToMerge = this.transformServerResourceToClientResource({
          ...response.data.resource,
          clientId: response.data.clientId || response.data.resource.clientId,
        });

        return fieldsToMerge ? resource.merge(fieldsToMerge) : resource;
      }

      return {
        ...resource,
        ...response?.data?.resource,
        clientId: response?.data?.clientId || response?.data?.resource?.clientId,
      };
    }
    if (response?.data?.id) {
      verify(typeof response?.data?.id === 'string', 'Response data id should be a string');
      // apply id
      return isRecordOf(resource) ? resource.set('id', response?.data?.id) : { ...resource, id: response?.data?.id };
    }
    return resource;
  },
  transformResourcesWithResponse(resources, response) {
    const errors: string[] = [];
    const newResources = response?.data?.items?.map((item) => {
      let resource: T | RecordOf<T> | undefined;
      if (item.resource?.status === 'ERROR' && item.errors) {
        errors.push(...item.errors.map((e) => JSON.stringify(e)));
      }
      const resourceItem = item.resource;
      const resourceUpdate = resourceItem?.resource;
      const clientId = resourceItem?.clientId || resourceUpdate?.clientId;
      const id = resourceItem?.id || resourceUpdate?.id;
      if (clientId) {
        resource = resources.find((fResource) => fResource.clientId === clientId);
        if (resource) {
          if (resourceUpdate) {
            if (isRecordOf(resource)) {
              const fieldsToMerge = this.transformServerResourceToClientResource({ ...resourceUpdate, clientId });
              resource = fieldsToMerge ? resource.merge(fieldsToMerge) : resource;
            } else {
              resource = { ...resource, ...resourceUpdate, clientId };
            }
          } else if (id) {
            resource = isRecordOf(resource) ? resource.set('id', id) : { ...resource, id };
          }
        } else {
          // biome-ignore lint/suspicious/noConsole: flags a response row that matches no stored resource
          console.error('transformResourcesWithResponse: clientId not found');
        }
      } else {
        resource = resources.find((fResource) => fResource.id === id);
      }
      return resource;
    });

    if (errors.length > 0) {
      throw errors;
    }

    return newResources;
  },

  // request transformers
  transformResourceToRequestData(resource) {
    return this.transformClientResourceToRequestData(resource);
  },
  transformResourcesToRequestData(resources) {
    return {
      resources: Array.from(resources.values(), (resource) => this.transformClientResourceToRequestData(resource)),
    };
  },

  successAction: undefined,
  failureAction: undefined,
});

export function createTypedQcsSyncResourcesConfigFactory<T extends BaseResource, Command = never>() {
  return Record<QcsSyncResourcesConfigProps<T, Command>>(getQcsSyncResourcesConfigDefaults<T>());
}
// Deprecated, use createTypedQcsSyncResourcesConfigFactory instead if you can
export const mkQcsSyncResourceConfig = createTypedQcsSyncResourcesConfigFactory<BaseResource>();
